/**
 * App store (Zustand). Holds auth state (the session user) plus a hydration
 * flag, and persists { user } to localStorage so a reload doesn't bounce you
 * to /login before rehydration. The real source of truth is the Django
 * session cookie — useSessionSync reconciles this store against it at boot.
 * The ProtectedRoute guard waits on `isHydrated`.
 */
import { create } from "zustand";
import { devtools, persist, createJSONStorage } from "zustand/middleware";
import type { User } from "@/features/auth/auth.types";

export type AppStore = {
  // --- auth ---
  user: User | null;
  setUser: (user: User | null) => void;
  clearAuth: () => void;
  // --- app lifecycle ---
  isHydrated: boolean;
  setHydrated: (hydrated: boolean) => void;
};

export const useStore = create<AppStore>()(
  devtools(
    persist(
      (set) => ({
        user: null,
        setUser: (user) => set({ user }),
        clearAuth: () => set({ user: null }),

        isHydrated: false,
        setHydrated: (isHydrated) => set({ isHydrated }),
      }),
      {
        name: "afell-engine-store",
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          user: state.user,
        }),
        onRehydrateStorage: () => (state) => {
          state?.setHydrated(true);
        },
      }
    ),
    { name: "afell-engine", enabled: import.meta.env.DEV }
  )
);

// --- selectors ---
export const useUser = () => useStore((s) => s.user);
export const useIsAuthenticated = () => useStore((s) => !!s.user);
export const useIsHydrated = () => useStore((s) => s.isHydrated);
